'use client'

import { useEffect, useRef, useSyncExternalStore } from 'react'
import { usePathname } from 'next/navigation'
import { YM_COUNTER_ID, isAnalyticsOn, subscribeAnalytics } from '@/lib/analytics'

// Счётчик Яндекс Метрики. Грузится, если от него не отказались (lib/analytics.ts).
// Вебвизор выключен сознательно: он записывает то, что человек печатает
// (реплики в чате, «Как сказать?», письмо в «Проверь моё сообщение»), а это
// тексты пользователя, которые в Яндекс мы не передаём.
//
// defer: true — счётчик не шлёт просмотр сам. Next меняет страницы без
// перезагрузки, поэтому просмотр отправляем вручную при каждой смене пути,
// с предыдущим адресом как referer. Смена только ?query просмотром не
// считается: метки utm приходят в адресе первой страницы, этого хватает.

type Ym = ((id: number, method: string, ...args: unknown[]) => void) & { a?: unknown[][]; l?: number }

declare global {
  interface Window {
    ym?: Ym
  }
}

// Домены, с которых пробуем взять tag.js, по порядку. Проверка с прода 27.09:
// один из узлов за mc.yandex.ru (87.250.255.224, его DNS Яндекса выдаёт части
// клиентов, в том числе из Турции) отдаёт страницу 404 вместо скрипта, и
// браузер режет её как не-JS (ERR_BLOCKED_BY_ORB). mc.yandex.md — домен самого
// Яндекса, он встроен в tag.js как запасной для отправки данных; с него
// скрипт приходил и тогда, когда основной адрес отвечал 404.
const TAG_HOSTS = ['mc.yandex.ru', 'mc.yandex.md']

function appendTag(id: number, hostIndex: number) {
  const host = TAG_HOSTS[hostIndex]
  if (!host) return
  const s = document.createElement('script')
  s.async = true
  s.src = `https://${host}/metrika/tag.js?id=${id}`
  // Отказ домена, после которого есть запасной, в журнал ошибок не пишем
  // (client-error-watch): записываем, только если не ответил последний.
  if (hostIndex < TAG_HOSTS.length - 1) s.dataset.errFallback = '1'
  s.onerror = () => {
    s.remove()
    appendTag(id, hostIndex + 1)
  }
  document.head.appendChild(s)
}

function loadCounter(id: number) {
  if (window.ym) return
  const ym: Ym = (...args) => {
    ;(ym.a = ym.a || []).push(args)
  }
  ym.l = Date.now()
  window.ym = ym
  // tag.js разбирается одной задачей на 400 мс на медленном телефоне (Lighthouse
  // 10.10) и задерживал первые нажатия на лендинге. Грузим его после load,
  // когда браузер свободен; init и просмотры до этого копятся в очереди ym.a.
  const start = () => {
    const idle = window.requestIdleCallback ?? ((cb: () => void) => window.setTimeout(cb, 300))
    idle(() => appendTag(id, 0), { timeout: 1000 })
  }
  if (document.readyState === 'complete') start()
  else window.addEventListener('load', start, { once: true })
  // ssr: true — из кода установки Метрики для сайтов с серверным рендерингом
  // (Next рендерит страницы на сервере).
  ym(id, 'init', {
    ssr: true,
    defer: true,
    clickmap: true,
    trackLinks: true,
    accurateTrackBounce: true,
    webvisor: false,
  })
}

export function YandexMetrika() {
  const granted = useSyncExternalStore(subscribeAnalytics, isAnalyticsOn, () => false)
  const pathname = usePathname()
  const prevUrl = useRef<string | null>(null)

  useEffect(() => {
    if (!granted || YM_COUNTER_ID === null) return
    loadCounter(YM_COUNTER_ID)
    const url = window.location.href
    if (url === prevUrl.current) return
    window.ym?.(YM_COUNTER_ID, 'hit', url, {
      title: document.title,
      referer: prevUrl.current ?? document.referrer,
    })
    prevUrl.current = url
  }, [granted, pathname])

  return null
}
